import * as React from "react";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto, ErrorCode, type ApiError } from "@racoon/api";
import { isPasswordLongEnough } from "@racoon/lib";
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  useSetUserPasswordMutation,
  roleKeyToEnum,
  roleEnumToKey,
  type RoleKey,
} from "~/shared/api/user/user.grpc";
import { useLazyListTeamsQuery, useUpdateTeamMutation } from "~/shared/api/team/team.grpc";
import { useAddUserToGroupMutation } from "~/shared/api/custom-groups/custom-groups.api";
import { useListOrganizationsQuery } from "~/shared/api/organization/organization.grpc";
import { getRoleOptions, getStaffRoleOptions } from "~/shared/lib/role-options";
import {
  formatPhone,
  phoneDigits,
  isCompletePhone,
  isPartialPhone,
  toPhonePayload,
} from "~/shared/lib/phone";

/** `team`'s members with `userId` added or removed: `user_ids` replaces the whole membership. */
export const teamMembersWith = (team: ConnectProto.Team, userId: bigint, member: boolean) => {
  const others = team.users.map((u) => u.id).filter((id) => id !== userId);
  return member ? [...others, userId] : others;
};

export type UserFormOrg =
  | { mode: "fixed"; id: string | number | bigint | null | undefined }
  | { mode: "picker" }
  | { mode: "readonly"; orgMap?: Map<string, string> };

interface UseUserFormArgs {
  open: boolean;
  user: ConnectProto.User | null;
  org: UserFormOrg;
  defaultRole?: RoleKey;
  staffRolesOnly?: boolean;
  onClose: () => void;
  onSaved?: () => void;
  /** Shown when the user clears an email that cannot be cleared through the API. */
  onEmailRequired: () => void;
}

/** State, derived validity and the save sequence for the employee form. */
export function useUserForm({
  open,
  user,
  org,
  defaultRole,
  staffRolesOnly,
  onClose,
  onSaved,
  onEmailRequired,
}: UseUserFormArgs) {
  const t = useT();
  const isEdit = !!(user && user.id);

  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [setUserPassword, { isLoading: isSettingPassword }] = useSetUserPasswordMutation();
  const [updateTeam] = useUpdateTeamMutation();
  const [listTeams] = useLazyListTeamsQuery();
  const [addUserToGroup] = useAddUserToGroupMutation();

  const [lastName, setLastName] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [role, setRole] = React.useState<RoleKey | "">("");
  const [driverLicense, setDriverLicense] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("+7");
  const [password, setPassword] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [pickedOrgId, setPickedOrgId] = React.useState("");
  /** Create only: team and access group ids to join once the account exists. */
  const [teamIds, setTeamIds] = React.useState<string[]>([]);
  const [groupIds, setGroupIds] = React.useState<string[]>([]);
  /** The account exists, the joins are still going: Save must not create it again. */
  const [isJoining, setIsJoining] = React.useState(false);

  const { data: orgData, isLoading: isOrgsLoading } = useListOrganizationsQuery(
    {},
    { skip: !open || org.mode !== "picker" },
  );
  const orgOptions = React.useMemo(
    () => (orgData?.organizations ?? []).map((o) => ({ id: String(o.id), name: o.name })),
    [orgData],
  );
  const roleOptions = React.useMemo(
    () => (staffRolesOnly ? getStaffRoleOptions(t) : getRoleOptions(t)),
    [t, staffRolesOnly],
  );

  React.useEffect(() => {
    if (!open) return;
    if (isEdit && user) {
      setLastName(user.lastName ?? "");
      setFirstName(user.firstName ?? "");
      setRole(roleEnumToKey(user.role));
      setDriverLicense(user.driverLicenseNumber ?? "");
      setEmail(user.email ?? "");
      // The mask is KZ-only; a longer foreign number is kept verbatim, not truncated.
      setPhone(
        phoneDigits(user.phoneNumber ?? "").length > 11
          ? user.phoneNumber
          : formatPhone(user.phoneNumber || "+7"),
      );
    } else {
      setLastName("");
      setFirstName("");
      setRole(defaultRole ?? "");
      setDriverLicense("");
      setEmail("");
      setPhone("+7");
      setPickedOrgId("");
    }
    setPassword("");
    setErrorMessage("");
  }, [open, user, isEdit, defaultRole]);

  const hasEmail = email.trim().length > 0;
  const hasPhone = isCompletePhone(phone);
  const isArchived = Number(user?.status) === ConnectProto.UserStatus.INACTIVE;

  const createOrgId =
    org.mode === "picker"
      ? pickedOrgId
        ? BigInt(pickedOrgId)
        : undefined
      : org.mode === "fixed" && org.id != null
        ? BigInt(org.id)
        : undefined;

  // A team holds one role of one org, and the role brings its own access group
  // (a pick of it would be granted twice): the picks go when either changes.
  React.useEffect(() => {
    setTeamIds([]);
    setGroupIds([]);
  }, [open, role, pickedOrgId]);

  // On edit the field means "leave blank to keep the current password".
  // Validate the trimmed value — it is what gets sent.
  const trimmedPassword = password.trim();
  const passwordOk = isEdit
    ? !trimmedPassword || isPasswordLongEnough(trimmedPassword)
    : isPasswordLongEnough(trimmedPassword);

  const canSubmit =
    !!firstName.trim() &&
    !!role &&
    !isPartialPhone(phone) &&
    // A new account is reached by phone; an existing one may predate that rule.
    (isEdit ? hasEmail || hasPhone : hasPhone) &&
    passwordOk &&
    (isEdit || createOrgId !== undefined);

  // Archiving is the card's own action: it already knows which employee.
  const toggleArchived = async () => {
    if (!user?.id) return;
    const userName = [user.firstName, user.lastName].filter(Boolean).join(" ");
    const confirmKey = isArchived ? "restore.confirm" : "archive.confirm";
    if (!window.confirm(t(Namespace.Employees, confirmKey, { userName }))) return;
    setErrorMessage("");
    try {
      await updateUser({
        id: user.id,
        status: isArchived ? ConnectProto.UserStatus.ACTIVE : ConnectProto.UserStatus.INACTIVE,
        ownerOrganizationId: user.ownerOrganizationId,
      }).unwrap();
      onClose();
      onSaved?.();
    } catch {
      setErrorMessage(t(Namespace.Employees, "error.generic"));
    }
  };

  // user_ids replaces the whole membership: build it from the teams as they are
  // now, not as they were when picked, or members added meanwhile would be dropped.
  const joinTeams = async (userId: bigint, orgId: bigint, roleValue: ConnectProto.UserRole) => {
    if (teamIds.length === 0) return;
    const { teams } = await listTeams({
      ownerOrganizationIds: [orgId],
      userRoles: [roleValue],
    }).unwrap();
    await Promise.allSettled(
      teams
        .filter((team) => teamIds.includes(String(team.id)))
        .map((team) =>
          updateTeam({ id: team.id, userIds: teamMembersWith(team, userId, true) }).unwrap(),
        ),
    );
  };

  const submit = async () => {
    const roleValue = roleKeyToEnum(role);
    if (roleValue === undefined) return;
    // Unset email means "leave as is" to the backend, so clearing one is a silent no-op.
    if (isEdit && !!user?.email && !hasEmail) {
      onEmailRequired();
      return;
    }
    setErrorMessage("");
    try {
      if (isEdit && user?.id) {
        await updateUser({
          id: user.id,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          role: roleValue,
          driverLicenseNumber: driverLicense || "",
          email: hasEmail ? email.trim() : undefined,
          ownerOrganizationId: user.ownerOrganizationId,
          // A complete number only — the seeded "+7" must not overwrite the record.
          ...(hasPhone ? { phoneNumber: toPhonePayload(phone) } : {}),
        }).unwrap();

        if (trimmedPassword && user.ownerOrganizationId) {
          await setUserPassword({
            id: user.id,
            ownerOrganizationId: user.ownerOrganizationId,
            newPassword: trimmedPassword,
          }).unwrap();
        }
      } else {
        if (createOrgId === undefined) return;
        const created = await createUser({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          role: roleValue,
          driverLicenseNumber: driverLicense || undefined,
          email: hasEmail ? email.trim() : `${phoneDigits(phone)}@phone.kz`,
          phoneNumber: toPhonePayload(phone),
          password: trimmedPassword,
          ownerOrganizationId: createOrgId,
        }).unwrap();
        // The account exists now: a failed join is reported by the global error
        // toaster and must not keep the form open to create the account twice.
        setIsJoining(true);
        try {
          await Promise.allSettled([
            joinTeams(created.id, createOrgId, roleValue),
            ...groupIds.map((id) =>
              addUserToGroup({
                userId: created.id,
                customGroupId: BigInt(id),
                role: "manager",
              }).unwrap(),
            ),
          ]);
        } finally {
          setIsJoining(false);
        }
      }

      onClose();
      onSaved?.();
    } catch (error) {
      const { code } = error as Partial<ApiError>;
      setErrorMessage(
        code === ErrorCode.ERROR_CODE_DUPLICATE_PHONE_NUMBER
          ? t(Namespace.Employees, "error.phoneExists")
          : code === ErrorCode.ERROR_CODE_DUPLICATE_EMAIL
            ? t(Namespace.Employees, "error.emailExists")
            : t(Namespace.Employees, "error.generic"),
      );
    }
  };

  return {
    isEdit,
    fields: {
      lastName,
      setLastName,
      firstName,
      setFirstName,
      role,
      setRole,
      driverLicense,
      setDriverLicense,
      email,
      setEmail,
      phone,
      setPhone,
      password,
      setPassword,
      pickedOrgId,
      setPickedOrgId,
      teamIds,
      setTeamIds,
      groupIds,
      setGroupIds,
    },
    createOrgId,
    orgOptions,
    isOrgsLoading,
    roleOptions,
    errorMessage,
    passwordOk,
    canSubmit,
    isSubmitting: isCreating || isUpdating || isSettingPassword || isJoining,
    isArchived,
    toggleArchived,
    submit,
  };
}
